import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError, shopLogoUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Upload, Loader2, ImageIcon, Wrench, Phone, Save, Megaphone, Plus, Pencil, Trash2, X, Hash, Palette, RotateCcw, Star } from "lucide-react";
import { toast } from "sonner";
import { applyBrandColor, BRAND_PRESETS, DEFAULT_BRAND_COLOR } from "@/components/BrandTheme";
import { BusinessHoursEditor } from "@/components/BusinessHours";
import { DefaultMarkupCard } from "@/components/erp/DefaultMarkup";
import { MechanicsCard } from "@/components/MechanicsCard";
import { BookingSettingsCard } from "@/components/BookingSettingsCard";
import { MessageTemplatesCard } from "@/components/MessageTemplatesCard";
import { PdfTermsCard } from "@/components/PdfTermsCard";
import { PixSettingsCard } from "@/components/PixSettingsCard";
import { PlateLookupCard } from "@/components/PlateLookupCard";
import { ExportDataCard } from "@/components/ExportDataCard";
import { OwnerDriveBackupCard } from "@/components/OwnerDriveBackupCard";
import { ServiceKitsCard } from "@/components/ServiceKits";
import { PaymentFeesCard } from "@/components/PaymentFeesCard";
import { WhatsAppSettings } from "@/components/WhatsAppSettings";
import { CompanyCard } from "@/components/CompanyCard";
import { ReportSettingsCard } from "@/components/ReportSettingsCard";
import { TechChecklistSettingsCard } from "@/components/TechChecklistSettings";
import { InspectionItemsCard, OilSettingsCard } from "@/components/SettingsExtras";

const SETTINGS_TABS = [["oficina", "Oficina"], ["atendimento", "Atendimento"], ["agendamento", "Agendamento"], ["financeiro", "Financeiro e fiscal"], ["whatsapp", "WhatsApp"]];
import { IntegrationsCard } from "@/components/fiscal/IntegrationsCard";
import { SpedSettingsCard } from "@/components/fiscal/SpedSettingsCard";

const MAX_PROMOS = 3;

function PromoImage({ promoId, bust = 0, className = "" }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    let revoke = null;
    (async () => {
      try {
        const resp = await api.get(`/promotions/${promoId}/image?v=${bust}`, { responseType: "blob" });
        const u = URL.createObjectURL(resp.data);
        revoke = u;
        setUrl(u);
      } catch {}
    })();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [promoId, bust]);
  if (!url) {
    return (
      <div className={`flex items-center justify-center bg-zinc-900 border border-zinc-800 ${className}`}>
        <Loader2 className="h-5 w-5 text-zinc-600 animate-spin" />
      </div>
    );
  }
  return <img src={url} alt="Promoção" className={`object-cover ${className}`} />;
}

export default function OwnerSettings() {
  const { user, refresh } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [bust, setBust] = useState(0);
  const [phone, setPhone] = useState("");
  const [savingPhone, setSavingPhone] = useState(false);
  const [nextNumber, setNextNumber] = useState("");
  const [savingNumber, setSavingNumber] = useState(false);
  const [brandColor, setBrandColor] = useState(DEFAULT_BRAND_COLOR);
  const [savingColor, setSavingColor] = useState(false);
  const [reviewUrl, setReviewUrl] = useState("");
  const [lostDays, setLostDays] = useState(90);
  const [savingReview, setSavingReview] = useState(false);
  const fileRef = useRef(null);

  const [promos, setPromos] = useState([]);
  const [loadingPromos, setLoadingPromos] = useState(true);
  const [promoDialogOpen, setPromoDialogOpen] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [promoDesc, setPromoDesc] = useState("");
  const [promoFile, setPromoFile] = useState(null);
  const [promoRemoveImage, setPromoRemoveImage] = useState(false);
  const [savingPromo, setSavingPromo] = useState(false);
  const [deletingPromoId, setDeletingPromoId] = useState("");
  const promoFileRef = useRef(null);

  const shop = user?.shop;
  const [tab, setTab] = useState("oficina");
  const hasLogo = shop?.logo_path;
  const logoUrl = shop?.id ? `${shopLogoUrl(shop.id)}?v=${bust}` : null;

  useEffect(() => { setPhone(shop?.phone || ""); }, [shop?.phone]);
  useEffect(() => { setNextNumber(shop?.next_order_number ?? ""); }, [shop?.next_order_number]);
  useEffect(() => { setBrandColor((shop?.brand_color || DEFAULT_BRAND_COLOR).toUpperCase()); }, [shop?.brand_color]);
  useEffect(() => { setReviewUrl(shop?.google_review_url || ""); setLostDays(shop?.lost_customer_days || 90); }, [shop?.google_review_url, shop?.lost_customer_days]);
  // Live preview while picking — only when the current value is a valid 6-digit hex. Reset to saved on unmount.
  useEffect(() => {
    if (/^#[0-9A-F]{6}$/.test(brandColor)) applyBrandColor(brandColor);
  }, [brandColor]);
  useEffect(() => {
    return () => { applyBrandColor(shop?.brand_color || DEFAULT_BRAND_COLOR); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [promoLoadError, setPromoLoadError] = useState("");
  const loadPromos = async () => {
    try {
      setLoadingPromos(true);
      setPromoLoadError("");
      const { data } = await api.get("/promotions");
      setPromos(data);
    } catch (e) {
      setPromoLoadError(formatApiError(e));
    } finally {
      setLoadingPromos(false);
    }
  };
  useEffect(() => { loadPromos(); }, []);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !shop?.id) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/shops/${shop.id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo atualizada");
      await refresh();
      setBust(Date.now());
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const savePhone = async () => {
    if (!shop?.id) return;
    setSavingPhone(true);
    try {
      await api.patch(`/shops/${shop.id}/phone`, { phone });
      await refresh();
      toast.success("Telefone atualizado");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingPhone(false);
    }
  };

  const saveNextNumber = async () => {
    if (!shop?.id) return;
    const n = parseInt(nextNumber, 10);
    if (!Number.isInteger(n) || n < 1) {
      toast.error("Informe um n\u00famero inteiro positivo");
      return;
    }
    setSavingNumber(true);
    try {
      await api.patch(`/shops/${shop.id}/next-order-number`, { next_order_number: n });
      await refresh();
      toast.success(`Pr\u00f3xima OS ser\u00e1 n\u00ba ${n}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingNumber(false);
    }
  };

  const saveBrandColor = async (color) => {
    if (!shop?.id) return;
    const c = (color || "").trim().toUpperCase();
    if (!/^#[0-9A-F]{6}$/.test(c)) {
      toast.error("Use um c\u00f3digo hex v\u00e1lido no formato #RRGGBB");
      return;
    }
    setSavingColor(true);
    try {
      await api.patch(`/shops/${shop.id}/brand-color`, { brand_color: c });
      await refresh();
      toast.success("Cor principal salva");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingColor(false);
    }
  };

  const resetBrandColor = async () => {
    setBrandColor(DEFAULT_BRAND_COLOR);
    await saveBrandColor(DEFAULT_BRAND_COLOR);
  };

  const saveReview = async () => {
    if (!shop?.id) return;
    setSavingReview(true);
    try {
      const d = parseInt(lostDays, 10);
      await api.patch(`/shops/${shop.id}/review-link`, { google_review_url: reviewUrl, lost_customer_days: Number.isFinite(d) ? d : 90 });
      await refresh();
      toast.success("Pós-venda atualizado");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingReview(false); }
  };

  const openCreatePromo = () => {
    setEditingPromo(null);
    setPromoDesc("");
    setPromoFile(null);
    setPromoRemoveImage(false);
    setPromoDialogOpen(true);
  };
  const openEditPromo = (p) => {
    setEditingPromo(p);
    setPromoDesc(p.description);
    setPromoFile(null);
    setPromoRemoveImage(false);
    setPromoDialogOpen(true);
  };

  const submitPromo = async (e) => {
    e.preventDefault();
    const desc = promoDesc.trim();
    if (!desc) {
      toast.error("Descrição é obrigatória");
      return;
    }
    setSavingPromo(true);
    try {
      const fd = new FormData();
      fd.append("description", desc);
      if (promoFile) fd.append("file", promoFile);
      if (editingPromo && promoRemoveImage && !promoFile) fd.append("remove_image", "1");
      const config = { headers: { "Content-Type": "multipart/form-data" } };
      if (editingPromo) {
        const { data } = await api.patch(`/promotions/${editingPromo.id}`, fd, config);
        setPromos((p) => p.map((x) => (x.id === data.id ? data : x)));
        toast.success("Promoção atualizada");
      } else {
        const { data } = await api.post("/promotions", fd, config);
        setPromos((p) => [data, ...p]);
        toast.success("Promoção criada");
      }
      setBust(Date.now());
      setPromoDialogOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingPromo(false);
    }
  };

  const deletePromo = async (p) => {
    setDeletingPromoId(p.id);
    try {
      await api.delete(`/promotions/${p.id}`);
      setPromos((prev) => prev.filter((x) => x.id !== p.id));
      toast.success("Promoção removida");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDeletingPromoId("");
    }
  };

  const canCreatePromo = promos.length < MAX_PROMOS;

  return (
    <div className="space-y-6 animate-fade-up max-w-3xl">
      <div>
        <div className="text-[11px] font-mono-caps text-zinc-500">Configurações</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black uppercase text-zinc-100">Oficina</h1>
        <p className="text-sm text-zinc-400 mt-1">
          Personalize a identidade visual, contatos e promoções da sua oficina.
        </p>
      </div>

      <div className="-mx-1 overflow-x-auto" data-testid="settings-tabs">
        <div className="flex gap-1 px-1 min-w-max border-b border-zinc-800">
          {SETTINGS_TABS.map(([k, l]) => (
            <button key={k} type="button" data-testid={`settings-tab-${k}`} onClick={() => setTab(k)}
              className={`px-4 h-11 text-sm font-bold whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === k ? "border-green-brand text-zinc-50" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}>{l}</button>
          ))}
        </div>
      </div>

      {tab === "atendimento" && shop && (<>
        <MechanicsCard />
        <MessageTemplatesCard key={`tpl-${shop.id}`} shop={shop} onSaved={refresh} />
        <PdfTermsCard key={`terms-${shop.id}`} shop={shop} onSaved={refresh} />
        <InspectionItemsCard key={`insp-${shop.id}`} />
        <TechChecklistSettingsCard key={`tc-${shop.id}`} />
        <OilSettingsCard key={`oil-${shop.id}`} shop={shop} onSaved={refresh} />
      </>)}
      {tab === "agendamento" && shop && <BookingSettingsCard slug={shop.slug} />}
      {tab === "financeiro" && shop && (<>
        <DefaultMarkupCard />
        <ReportSettingsCard />
        <PixSettingsCard key={`pix-${shop.id}`} shop={shop} onSaved={refresh} />
        <PlateLookupCard key={`plate-${shop.id}`} />
        <IntegrationsCard />
        <SpedSettingsCard key={`sped-${shop.id}`} />
      </>)}
      {tab !== "oficina" && !shop && <div className="text-sm text-amber-300">Escolha uma unidade no seletor do topo.</div>}

      {tab === "oficina" && (<>
      {shop && <CompanyCard key={`company-${shop.id}`} />}
      {shop && <BusinessHoursEditor shop={shop} onSaved={refresh} />}

      <div className="rounded-xl border border-zinc-800 bg-zinc-950/70 p-5 md:p-7">
        <div className="flex items-center gap-2 mb-4"><Star className="h-4 w-4 text-amber-brand" /><h2 className="font-display text-lg font-bold uppercase text-zinc-100">Pós-venda e Google</h2></div>
        <p className="text-sm text-zinc-400 mb-3">Cole o link para o cliente avaliar sua oficina no Google (botão "Ver avaliação" ou "Avaliar no Google"). Configure também em quantos dias sem OS consideramos o cliente "sumido".</p>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px] gap-3">
          <div>
            <Label className="text-xs font-mono-caps text-zinc-400">Link de avaliação do Google</Label>
            <Input data-testid="shop-review-url-input" value={reviewUrl} onChange={(e) => setReviewUrl(e.target.value)} className="mt-1 bg-zinc-900 border-zinc-800" placeholder="https://g.page/r/..." />
          </div>
          <div>
            <Label className="text-xs font-mono-caps text-zinc-400">Cliente sumido (dias)</Label>
            <Input data-testid="shop-lost-days-input" type="number" min={7} max={365} value={lostDays} onChange={(e) => setLostDays(e.target.value)} className="mt-1 bg-zinc-900 border-zinc-800" />
          </div>
        </div>
        <div className="mt-3">
          <Button data-testid="shop-review-save-btn" onClick={saveReview} disabled={savingReview} className="h-11 bg-green-brand hover:bg-green-brandDark text-white font-bold uppercase">
            {savingReview ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Save className="h-4 w-4 mr-2" /> Salvar pós-venda</>)}
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-zinc-800 bg-zinc-950/70 p-5 md:p-7">
        <div className="flex items-center gap-2 mb-4">
          <ImageIcon className="h-4 w-4 text-green-brand" />
          <h2 className="font-display text-lg font-bold uppercase text-zinc-100">Logotipo</h2>
        </div>
        <div className="flex items-center gap-5">
          <div
            data-testid="shop-logo-preview"
            className="h-24 w-24 rounded-xl border border-zinc-800 bg-zinc-900 flex items-center justify-center overflow-hidden"
          >
            {hasLogo ? (
              <img src={logoUrl} alt={shop?.name || "Logo"} className="h-full w-full object-cover" />
            ) : (
              <Wrench className="h-10 w-10 text-zinc-700" />
            )}
          </div>
          <div className="flex-1">
            <div className="text-sm text-zinc-300 font-semibold">{shop?.name}</div>
            <div className="text-xs text-zinc-500 mt-0.5">
              Formatos aceitos: PNG, JPG, WEBP, SVG. Até 3MB.
            </div>
            <input
              ref={fileRef}
              data-testid="logo-upload-input"
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
              className="hidden"
              onChange={onUpload}
            />
            <Button
              data-testid="logo-upload-btn"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="mt-3 bg-green-brand hover:bg-green-brandDark text-white font-bold uppercase"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Upload className="h-4 w-4 mr-2" /> Enviar logo</>)}
            </Button>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-zinc-800 bg-zinc-950/70 p-5 md:p-7">
        <div className="flex items-center gap-2 mb-4">
          <Phone className="h-4 w-4 text-green-brand" />
          <h2 className="font-display text-lg font-bold uppercase text-zinc-100">Telefone de contato</h2>
        </div>
        <p className="text-sm text-zinc-400 mb-3">
          Exibido para o cliente no painel dele, com botões para ligar e abrir o WhatsApp.
          Use DDD + número (ex.: 11999998888). O código do Brasil (+55) é adicionado automaticamente no link do WhatsApp.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="flex-1">
            <Label className="text-xs font-mono-caps text-zinc-400">Telefone</Label>
            <Input
              data-testid="shop-phone-input"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="mt-1 bg-zinc-900 border-zinc-800"
              placeholder="11999998888"
              inputMode="tel"
            />
          </div>
          <Button
            data-testid="shop-phone-save-btn"
            onClick={savePhone}
            disabled={savingPhone}
            className="h-11 bg-green-brand hover:bg-green-brandDark text-white font-bold uppercase"
          >
            {savingPhone ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Save className="h-4 w-4 mr-2" /> Salvar</>)}
          </Button>
        </div>
      </div>

      <div className="rounded-2xl border border-card2-border bg-card2 p-5 md:p-7">
        <div className="flex items-center gap-2 mb-4">
          <Palette className="h-4 w-4 text-green-neon" />
          <h2 className="font-display text-lg font-bold uppercase text-zinc-100">Cor principal</h2>
        </div>
        <p className="text-sm text-zinc-400 mb-4">
          A cor é aplicada em botões, cabeçalho e destaques, para você e para os seus clientes. Escolha um dos tons prontos ou use o código hex da sua marca.
        </p>

        <div className="grid grid-cols-4 sm:grid-cols-8 gap-2 mb-4">
          {BRAND_PRESETS.map((p) => {
            const active = p.value.toUpperCase() === brandColor.toUpperCase();
            return (
              <button
                key={p.value}
                type="button"
                data-testid={`brand-color-preset-${p.value.replace("#", "").toLowerCase()}`}
                onClick={() => setBrandColor(p.value)}
                title={p.label}
                aria-label={p.label}
                className={`h-10 w-full rounded-xl border-2 transition-transform ${active ? "border-white scale-105" : "border-card2-border hover:scale-105"}`}
                style={{ backgroundColor: p.value }}
              />
            );
          })}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="flex-1">
            <Label className="text-xs font-mono-caps text-zinc-400">Código hex</Label>
            <div className="mt-1 flex items-center gap-2">
              <span
                data-testid="brand-color-swatch"
                className="h-10 w-10 rounded-xl border border-card2-border shrink-0"
                style={{ backgroundColor: brandColor }}
              />
              <Input
                data-testid="brand-color-hex-input"
                value={brandColor}
                onChange={(e) => {
                  let v = e.target.value.toUpperCase();
                  if (v && !v.startsWith("#")) v = "#" + v;
                  setBrandColor(v.slice(0, 7));
                }}
                className="bg-appbg-soft border-card2-border uppercase"
                placeholder="#22C55E"
                maxLength={7}
              />
            </div>
          </div>
          <Button
            data-testid="brand-color-save"
            onClick={() => saveBrandColor(brandColor)}
            disabled={savingColor}
            className="h-11 rounded-2xl bg-gradient-to-b from-green-neon to-green-brandDark font-bold uppercase tracking-wide shadow-[0_8px_28px_-10px_rgba(0,0,0,0.55)] hover:brightness-110"
          >
            {savingColor ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Save className="h-4 w-4 mr-2" /> Salvar cor</>)}
          </Button>
          <Button
            data-testid="brand-color-reset"
            onClick={resetBrandColor}
            variant="outline"
            disabled={savingColor}
            className="h-11 bg-transparent border-card2-border text-zinc-300 hover:bg-card2 hover:text-zinc-100"
          >
            <RotateCcw className="h-4 w-4 mr-2" /> Padrão
          </Button>
        </div>

        <div data-testid="brand-color-preview" className="mt-5 rounded-2xl border border-card2-border bg-appbg-soft p-4">
          <div className="text-[11px] font-mono-caps text-zinc-500 mb-2">Prévia</div>
          <div className="flex flex-wrap items-center gap-3">
            <Button className="h-10 px-4 rounded-xl bg-green-brand hover:bg-green-brandDark font-bold uppercase">Botão principal</Button>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-green-brand/40 bg-green-brand/10 text-green-neon px-2 py-0.5 text-xs font-bold tracking-wider">OS #1521</span>
            <span className="text-xs text-green-neon font-semibold">Link em destaque</span>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-card2-border bg-card2 p-5 md:p-7">
        <div className="flex items-center gap-2 mb-4">
          <Hash className="h-4 w-4 text-green-neon" />
          <h2 className="font-display text-lg font-bold uppercase text-zinc-100">Numeração das ordens</h2>
        </div>
        <p className="text-sm text-zinc-400 mb-3">
          Defina o número da <strong>próxima</strong> OS. Ex.: se o seu bloco de papel está na OS 1520, coloque <strong>1521</strong>. A partir daí, cada ordem nova sobe +1 automaticamente. Não é possível voltar para um número já usado por esta oficina.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="flex-1">
            <Label className="text-xs font-mono-caps text-zinc-400">Próxima OS será</Label>
            <Input
              data-testid="shop-next-number-input"
              type="number"
              min={1}
              inputMode="numeric"
              value={nextNumber}
              onChange={(e) => setNextNumber(e.target.value)}
              className="mt-1 bg-appbg-soft border-card2-border"
              placeholder="1521"
            />
          </div>
          <Button
            data-testid="shop-next-number-save"
            onClick={saveNextNumber}
            disabled={savingNumber}
            className="h-11 rounded-2xl bg-gradient-to-b from-green-neon to-green-brandDark text-zinc-950 font-bold uppercase tracking-wide shadow-[0_8px_28px_-10px_rgba(74,222,128,0.55)] hover:brightness-110"
          >
            {savingNumber ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Save className="h-4 w-4 mr-2" /> Salvar</>)}
          </Button>
        </div>
      </div>
      </>)}

      {tab === "atendimento" && (
      <div className="rounded-xl border border-zinc-800 bg-zinc-950/70 p-5 md:p-7">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="flex items-center gap-2">
              <Megaphone className="h-4 w-4 text-amber-brand" />
              <h2 className="font-display text-lg font-bold uppercase text-zinc-100">Promoções</h2>
            </div>
            <p className="text-sm text-zinc-400 mt-1">
              Até <strong className="text-amber-brand">{MAX_PROMOS}</strong> promoções ativas aparecem no painel dos seus clientes.
            </p>
          </div>
          <Button
            data-testid="promo-new-btn"
            onClick={openCreatePromo}
            disabled={!canCreatePromo}
            className="bg-amber-brand hover:bg-amber-brandDark text-black font-bold uppercase"
          >
            <Plus className="h-4 w-4 mr-1" /> Nova promoção
          </Button>
        </div>

        {loadingPromos ? (
          <div className="flex items-center justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-amber-brand" /></div>
        ) : promoLoadError ? (
          <div data-testid="promos-load-error" className="rounded-xl border border-amber-800 bg-amber-950/30 p-4 text-sm text-amber-200 flex items-center justify-between gap-3">
            <span>Não foi possível carregar as promoções agora. {promoLoadError}</span>
            <Button type="button" size="sm" variant="outline" onClick={loadPromos} className="bg-zinc-900 border-zinc-700">Tentar de novo</Button>
          </div>
        ) : promos.length === 0 ? (
          <div data-testid="promos-empty" className="rounded-xl border border-dashed border-zinc-800 bg-zinc-950/40 p-8 text-center">
            <Megaphone className="h-8 w-8 mx-auto text-zinc-700" />
            <p className="mt-2 text-sm text-zinc-500">Nenhuma promoção cadastrada ainda.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {promos.map((p) => (
              <div key={p.id} data-testid={`promo-card-${p.id}`} className="rounded-xl border border-zinc-800 bg-zinc-900/50 overflow-hidden">
                {p.has_image ? (
                  <PromoImage promoId={p.id} bust={bust} className="h-40 w-full" />
                ) : (
                  <div className="h-40 w-full flex items-center justify-center bg-gradient-to-br from-amber-900/40 to-zinc-900">
                    <Megaphone className="h-10 w-10 text-amber-brand/70" />
                  </div>
                )}
                <div className="p-4">
                  <p className="text-sm text-zinc-200 whitespace-pre-wrap">{p.description}</p>
                  <div className="mt-3 flex items-center gap-2">
                    <Button
                      data-testid={`promo-edit-${p.id}`}
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditPromo(p)}
                      className="text-amber-300 hover:bg-amber-950/40"
                    >
                      <Pencil className="h-3.5 w-3.5 mr-1" /> Editar
                    </Button>
                    <Button
                      data-testid={`promo-delete-${p.id}`}
                      variant="ghost"
                      size="sm"
                      disabled={deletingPromoId === p.id}
                      onClick={() => deletePromo(p)}
                      className="text-red-400 hover:bg-red-950/40"
                    >
                      {deletingPromoId === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (<><Trash2 className="h-3.5 w-3.5 mr-1" /> Excluir</>)}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      <Dialog open={promoDialogOpen} onOpenChange={setPromoDialogOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display uppercase flex items-center gap-2">
              <Megaphone className="h-5 w-5 text-amber-brand" />
              {editingPromo ? "Editar promoção" : "Nova promoção"}
            </DialogTitle>
            <DialogDescription className="text-zinc-500">
              Imagem (opcional) + descrição que o cliente verá no painel dele.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitPromo} className="space-y-4" data-testid="promo-form">
            <div>
              <Label className="text-xs font-mono-caps text-zinc-400">Descrição</Label>
              <Textarea
                data-testid="promo-desc-input"
                required
                maxLength={500}
                rows={4}
                value={promoDesc}
                onChange={(e) => setPromoDesc(e.target.value)}
                placeholder="Troca de óleo com 15% off neste mês!"
                className="mt-1 bg-zinc-900 border-zinc-800"
              />
              <div className="text-[11px] text-zinc-500 mt-1 text-right">{promoDesc.length}/500</div>
            </div>
            <div>
              <Label className="text-xs font-mono-caps text-zinc-400">Imagem (opcional, até 5MB)</Label>
              {editingPromo?.has_image && !promoFile && !promoRemoveImage && (
                <div className="mt-2 relative">
                  <PromoImage promoId={editingPromo.id} bust={bust} className="h-32 w-full rounded-lg" />
                  <button
                    type="button"
                    data-testid="promo-remove-image-btn"
                    onClick={() => setPromoRemoveImage(true)}
                    className="absolute top-1.5 right-1.5 h-7 w-7 rounded-full bg-black/70 border border-zinc-700 text-zinc-200 flex items-center justify-center"
                    aria-label="Remover imagem"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
              <input
                ref={promoFileRef}
                data-testid="promo-image-input"
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                className="hidden"
                onChange={(e) => { setPromoFile(e.target.files?.[0] || null); setPromoRemoveImage(false); }}
              />
              <Button
                type="button"
                data-testid="promo-image-pick"
                onClick={() => promoFileRef.current?.click()}
                variant="ghost"
                className="mt-2 h-10 border border-zinc-800 text-zinc-200 hover:bg-zinc-900"
              >
                <Upload className="h-4 w-4 mr-2" />
                {promoFile ? promoFile.name : (editingPromo?.has_image && !promoRemoveImage ? "Trocar imagem" : "Selecionar imagem")}
              </Button>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setPromoDialogOpen(false)}
                className="text-zinc-300 hover:bg-zinc-900"
                disabled={savingPromo}
              >
                Cancelar
              </Button>
              <Button
                data-testid="promo-submit-btn"
                type="submit"
                disabled={savingPromo}
                className="bg-amber-brand hover:bg-amber-brandDark text-black font-bold uppercase"
              >
                {savingPromo ? <Loader2 className="h-4 w-4 animate-spin" /> : (editingPromo ? "Salvar alterações" : "Criar promoção")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {tab === "oficina" && (
      <div className="rounded-xl border border-zinc-800 bg-zinc-950/70 p-5 md:p-7">
        <h2 className="font-display text-lg font-bold uppercase text-zinc-100 mb-3">Informações da oficina</h2>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div>
            <dt className="text-[11px] font-mono-caps text-zinc-500">Nome</dt>
            <dd className="text-zinc-100 font-medium mt-0.5">{shop?.name || "—"}</dd>
          </div>
          <div>
            <dt className="text-[11px] font-mono-caps text-zinc-500">E-mail do dono</dt>
            <dd className="text-zinc-100 font-medium mt-0.5">{user?.email}</dd>
          </div>
          <div>
            <dt className="text-[11px] font-mono-caps text-zinc-500">Status</dt>
            <dd className="text-green-300 font-medium mt-0.5">{shop?.is_active ? "Ativa" : "Suspensa"}</dd>
          </div>
        </dl>
      </div>
      )}
      {tab === "financeiro" && <PaymentFeesCard />}
      {tab === "whatsapp" && <WhatsAppSettings />}
      {tab === "oficina" && <ServiceKitsCard />}
      {tab === "oficina" && <ExportDataCard />}
      {tab === "oficina" && shop && <OwnerDriveBackupCard />}
    </div>
  );
}
